import { ref } from 'vue'
import { syncUserFromFirestore, syncOrgMembersFromFirestore, syncUserOrgsDataFromFirestore, getUserOrgs } from '@/services/db'

const KEYS = {
  realUserId: 'impersonating_realUserId',
  realOrgId: 'impersonating_realOrgId',
  targetName: 'impersonating_targetName'
} as const

const isImpersonating = ref(!!localStorage.getItem(KEYS.realUserId))
const impersonatedName = ref(localStorage.getItem(KEYS.targetName) ?? '')

async function startImpersonation(
  targetUserId: string,
  targetName: string,
  activeOrgId: { value: string | null }
) {
  const realUserId = localStorage.getItem('currentUserId')
  const realOrgId = localStorage.getItem('activeOrgId')
  if (!realUserId) return

  // Save real state
  localStorage.setItem(KEYS.realUserId, realUserId)
  if (realOrgId) localStorage.setItem(KEYS.realOrgId, realOrgId)
  localStorage.setItem(KEYS.targetName, targetName)

  // Swap to target user
  localStorage.setItem('currentUserId', targetUserId)
  localStorage.removeItem('activeOrgId')

  // Sync target user's profile to IndexedDB (required for router auth check)
  await syncUserFromFirestore(targetUserId)

  // Sync target user's data
  await syncOrgMembersFromFirestore(targetUserId)
  await syncUserOrgsDataFromFirestore(targetUserId)

  // Set their first org as active
  const orgs = await getUserOrgs(targetUserId)
  if (orgs.length > 0 && orgs[0]) {
    activeOrgId.value = orgs[0].id
    localStorage.setItem('activeOrgId', orgs[0].id)
  } else {
    activeOrgId.value = null
  }

  isImpersonating.value = true
  impersonatedName.value = targetName
}

async function stopImpersonation(activeOrgId: { value: string | null }) {
  const realUserId = localStorage.getItem(KEYS.realUserId)
  const realOrgId = localStorage.getItem(KEYS.realOrgId)
  if (!realUserId) return

  // Restore real state
  localStorage.setItem('currentUserId', realUserId)
  if (realOrgId) {
    localStorage.setItem('activeOrgId', realOrgId)
    activeOrgId.value = realOrgId
  } else {
    localStorage.removeItem('activeOrgId')
    activeOrgId.value = null
  }

  // Clean up impersonation keys
  localStorage.removeItem(KEYS.realUserId)
  localStorage.removeItem(KEYS.realOrgId)
  localStorage.removeItem(KEYS.targetName)

  // Re-sync real admin data
  await syncOrgMembersFromFirestore(realUserId)
  await syncUserOrgsDataFromFirestore(realUserId)

  if (!activeOrgId.value) {
    const orgs = await getUserOrgs(realUserId)
    if (orgs.length > 0 && orgs[0]) {
      activeOrgId.value = orgs[0].id
      localStorage.setItem('activeOrgId', orgs[0].id)
    }
  }

  isImpersonating.value = false
  impersonatedName.value = ''
}

/** Get the real superadmin userId even during impersonation */
function getRealUserId(): string | null {
  return localStorage.getItem(KEYS.realUserId) || localStorage.getItem('currentUserId')
}

export function useImpersonation() {
  return {
    isImpersonating,
    impersonatedName,
    startImpersonation,
    stopImpersonation,
    getRealUserId
  }
}
